
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, Image, Clock, Loader2 } from "lucide-react";

interface ContentFormProps {
  newContent: {
    title: string;
    type: string;
    credits: number;
    isPublic: boolean;
    isPremiere: boolean;
    file: File | null;
    duration: string;
    thumbnail: File | null;
  };
  onContentChange: (content: {
    title: string;
    type: string;
    credits: number;
    isPublic: boolean;
    isPremiere: boolean;
    file: File | null;
    duration: string;
    thumbnail: File | null;
  }) => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onThumbnailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAddContent: () => void;
  isSubmitting: boolean;
}

export function ContentForm({
  newContent,
  onContentChange,
  onFileChange,
  onThumbnailChange,
  onAddContent,
  isSubmitting
}: ContentFormProps) {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="content-title">Τίτλος</Label>
        <Input 
          id="content-title" 
          value={newContent.title}
          onChange={(e) => onContentChange({...newContent, title: e.target.value})}
          className="mt-1"
          disabled={isSubmitting}
        />
      </div>
      <div>
        <Label htmlFor="content-type">Τύπος</Label>
        <select 
          id="content-type"
          className="w-full h-10 px-3 py-2 bg-background border border-input rounded-md"
          value={newContent.type}
          onChange={(e) => onContentChange({...newContent, type: e.target.value})}
          disabled={isSubmitting}
        >
          <option value="video">Βίντεο</option>
          <option value="document">Έγγραφο</option>
          <option value="course">Μάθημα</option>
        </select>
      </div>
      <div>
        <Label htmlFor="content-credits">Credits</Label>
        <Input 
          id="content-credits" 
          type="number" 
          min="1"
          value={newContent.credits}
          onChange={(e) => onContentChange({...newContent, credits: parseInt(e.target.value) || 5})}
          className="mt-1"
          disabled={isSubmitting}
        />
      </div>
      
      {(newContent.type === 'video' || newContent.type === 'course') && (
        <div>
          <Label htmlFor="content-duration" className="flex items-center">
            <Clock className="h-4 w-4 mr-2" /> Διάρκεια (λεπτά:δευτερόλεπτα)
          </Label>
          <Input 
            id="content-duration" 
            placeholder="π.χ. 10:30"
            value={newContent.duration}
            onChange={(e) => onContentChange({...newContent, duration: e.target.value})}
            className="mt-1"
            disabled={isSubmitting}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Εισάγετε τη διάρκεια σε μορφή λεπτά:δευτερόλεπτα (π.χ. 10:30)
          </p>
        </div>
      )}

      <div>
        <Label htmlFor="content-file" className="block mb-2">Αρχείο</Label>
        <div className="flex">
          <Input 
            id="content-file"
            type="file"
            onChange={onFileChange}
            className="flex-1"
            disabled={isSubmitting}
          />
          <Button variant="outline" className="ml-2" disabled={isSubmitting}>
            <Upload className="h-4 w-4 mr-1" /> Upload
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {newContent.file ? `Επιλεγμένο: ${newContent.file.name}` : 'Δεν έχει επιλεγεί αρχείο'}
        </p>
      </div>

      {newContent.type === 'video' && (
        <div>
          <Label htmlFor="content-thumbnail" className="flex items-center mb-2">
            <Image className="h-4 w-4 mr-2" /> Εικόνα Προεπισκόπησης
          </Label>
          <div className="flex">
            <Input 
              id="content-thumbnail"
              type="file"
              accept="image/*"
              onChange={onThumbnailChange}
              className="flex-1"
              disabled={isSubmitting}
            />
            <Button variant="outline" className="ml-2" disabled={isSubmitting}>
              <Image className="h-4 w-4 mr-1" /> Upload
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {newContent.thumbnail ? `Επιλεγμένη εικόνα: ${newContent.thumbnail.name}` : 'Δεν έχει επιλεγεί εικόνα προεπισκόπησης'}
          </p>
          {newContent.thumbnail && (
            <div className="mt-2 border rounded overflow-hidden" style={{ maxWidth: '200px' }}>
              <img
                src={URL.createObjectURL(newContent.thumbnail)}
                alt="Προεπισκόπηση thumbnail"
                className="w-full h-auto"
              />
            </div>
          )}
        </div>
      )}
      
      <div className="flex items-center space-x-2">
        <Checkbox 
          id="content-public" 
          checked={newContent.isPublic}
          onCheckedChange={(checked) => onContentChange({...newContent, isPublic: !!checked})}
          disabled={isSubmitting}
        />
        <Label htmlFor="content-public">Δημόσιο</Label>
      </div>
      <div className="flex items-center space-x-2">
        <Checkbox 
          id="content-premiere" 
          checked={newContent.isPremiere}
          onCheckedChange={(checked) => onContentChange({...newContent, isPremiere: !!checked})}
          disabled={isSubmitting}
        />
        <Label htmlFor="content-premiere">Πρεμιέρα (θα σταλεί ειδοποίηση)</Label>
      </div>
      <Button 
        className="w-full" 
        onClick={onAddContent}
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Υποβολή...
          </>
        ) : (
          'Προσθήκη Περιεχομένου'
        )}
      </Button>
    </div>
  );
}
